<template>
  <v-form v-model="valid" ref="form">

    <v-container fluid class="px-6 mt-4" @click="selectedItem = null">
      <v-row>
        <v-col cols="6" sm="4" md="3">
          <date-picker label="As of" v-model="product.initialAsof"/>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <outlined-block title="Initial quantities/values for new products" :rules="[initValid]">
            <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                          :items="product.initial" no-data-text="No products specified"
                          :headers="headers"
                          class="no-sort"
                          :item-class="item => item.id=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                          @click.native.stop=""
                          @click:row="item => {selectedItem = item.id; $nextTick(() => this.$refs.form.validate())}">
              <template v-slot:item.add="{index}">
                <v-btn class="mt-n2" icon @click.stop="deleteProduct(index)"><v-icon small>mdi-delete</v-icon></v-btn>
              </template>
              <template v-slot:item.site="{item}">
                <v-autocomplete v-if="selectedItem === item.id" :items="sites" v-model="item.siteId" dense item-text="name" item-value="id" outlined label="Site"/>
                <div v-else class="ml-1">{{sites.find(site => site.id === item.siteId).name}}</div>
              </template>
              <template v-slot:item.lot="{item}">
                <v-text-field v-if="selectedItem === item.id" v-model="item.lot" dense :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined label="Lot"/>
                <div v-else class="ml-1" :class="{'error--text':!item.lot}">{{item.lot || 'Required'}}</div>
              </template>
              <template v-slot:item.bondedQuantity="{item}">
                <template v-if="sites.find(s => s.id === item.siteId).B262">
                  <decimal-number-input v-if="selectedItem === item.id"
                                        dense v-model="item.bondedBottles" prefix="" suffix="bottles" :precision="0" outlined label="Bonded quantity" integer/>
                  <div v-else class="mr-1" :class="{'error--text':item.bondedBottles<0}">{{item.bondedBottles | number(0)}}</div>
                </template>
                <template v-else>n/a</template>
              </template>
              <template v-slot:item.bondedValue="{item}">
                <template v-if="sites.find(s => s.id === item.siteId).B262">
                  <decimal-number-input v-if="selectedItem === item.id"
                                        dense v-model="item.bondedValue" outlined label="Bonded value"/>
                  <div v-else class="mr-1" :class="{'error--text':item.bondedValue<0}">{{item.bondedValue | currency}}</div>
                </template>
                <template v-else>n/a</template>
              </template>
              <template v-slot:item.dutyPaidQuantity="{item}">
                <decimal-number-input v-if="selectedItem === item.id" dense v-model="item.dutyPaidBottles" prefix="" suffix="bottles" :precision="0" integer outlined label="Duty-paid quantity"/>
                <div v-else class="mr-1" :class="{'error--text':item.dutyPaidBottles<0}">{{item.dutyPaidBottles | number(0)}}</div>
              </template>
              <template v-slot:item.dutyPaidValue="{item}">
                <decimal-number-input v-if="selectedItem === item.id" dense v-model="item.dutyPaidValue" outlined label="Duty-paid value"/>
                <div v-else class="mr-1" :class="{'error--text':item.dutyPaidValue<0}">{{item.dutyPaidValue | currency}}</div>
              </template>
              <template v-slot:body.append="{headers}">
                <tr class="no-hover" @click="selectedItem = null">
                  <td class="text-center">
                    <v-btn fab color="primary" x-small @click.stop="addProduct"><v-icon small>mdi-plus</v-icon></v-btn>
                  </td>
                  <td :colspan="headers.length-1" class="text-caption text-right">
                    <sup>*</sup>Values are total values for the added inventory, not unit cost
                  </td>
                </tr>
              </template>
            </v-data-table>
          </outlined-block>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
export default {
  name: 'InventoryProductsEditInit',
  props: {
    product: {type:Object, required:true},
  },
  data() {
    return {
      valid: true,
      selectedItem: null
    }
  },
  computed: {
    sites() {
      return this.$store.getters.sites(true);
    },
    company() {
      return this.$store.getters.company;
    },
    headers() {
      const headers = [{text:'',value:'add',width:'5%',align:'center',cellClass:'pa-0',divider:true}];
      if (this.sites.length > 1)
        headers.push({text:'Site',value:'site',width:'20%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'});
      if (this.company.settings.lotTrackingForProducts)
        headers.push({text:'Lot #',value:'lot',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'});
      if (this.product.alcoholType!=='NonExcise')
        headers.push( {text:'Bonded quantity',value:'bondedQuantity',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'},
                      {text:'Bonded value *',value:'bondedValue',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'});
      return [...headers, {text:'Duty-paid quantity',value:'dutyPaidQuantity',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'},
                          {text:'Duty-paid value *',value:'dutyPaidValue',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'}];
    },
    initValid() {
      return this.product.initial.every(product => !!product.lot &&
                                                   product.bondedBottles >= 0 && product.bondedValue >= 0 &&
                                                   product.dutyPaidBottles >= 0 && product.dutyPaidValue >= 0);
    }
  },
  watch: {
    product: {
      handler() {this.$emit('input', true); this.valid = this.initValid},
      immediate: true,
      deep: true
    }
  },
  created() {
    this.valid = this.initValid;
  },
  methods: {
    async addProduct() {
      let lot = 'L0';
      if (this.company.settings.lotTrackingForProducts) {
        const results = await this.$apiCall(`query InventoryProductsEditInit {
                                                lotUnique(prefix:"P")
                                              }`,
                                          {});
        if (results.error)
          return;
        lot = results.data.lotUnique;
      }
      const product = {siteId:this.sites[0].id,lot,bondedBottles:0,bondedValue:0,dutyPaidBottles:0,dutyPaidValue:0,id:Date.now()};
      this.product.initial.push(product);
      this.selectedItem = product.id;
      this.$nextTick(() => this.$refs.form.validate());
    },
    deleteProduct(index) {
      this.product.initial.splice(index, 1);
    },
  }
}
</script>